import type { User } from '@/types';
import axios from 'axios';

/**
 * What this device remembers so the app can open without a connection: the signed-in person,
 * for this address only, for up to a week since the server last confirmed the session.
 * Signing out forgets it, and so does the server refusing the session.
 */
const SESSION_KEY = 'fh:offline-session';
export const OFFLINE_SESSION_DAYS = 7;

interface SavedSession {
  host: string;
  savedAt: number;
  user: User;
}

/** Separates one person's saved data from another's on a shared device. */
export function offlineIdentity(user: Pick<User, 'id' | 'tenantId'>): string {
  return `${user.tenantId}:${user.id}`;
}

export function saveOfflineSession(user: User) {
  // The platform admin works across workspaces; there is no one workspace to keep offline.
  if (user.role === 'SUPER_ADMIN') return;
  try {
    const saved: SavedSession = { host: window.location.host, savedAt: Date.now(), user };
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(saved));
  } catch {
    // Without storage the app simply needs a connection to open.
  }
}

export function readOfflineSession(now = Date.now()): User | null {
  try {
    const saved = JSON.parse(window.localStorage.getItem(SESSION_KEY) ?? 'null') as SavedSession | null;
    if (!saved?.user?.id || saved.host !== window.location.host) return null;
    if (now - saved.savedAt > OFFLINE_SESSION_DAYS * 86_400_000) return null;
    return saved.user;
  } catch {
    return null;
  }
}

export function clearOfflineSession() {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Nothing was saved.
  }
}

/** No answer at all: the device is offline or the server cannot be reached. */
export function isNetworkError(error: unknown): boolean {
  return axios.isAxiosError(error) && !error.response && error.code !== 'ERR_CANCELED';
}

/** The server answered and turned the session down, as opposed to not answering. */
export function isSessionRefused(error: unknown): boolean {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 400 || status === 401 || status === 403;
}
